import store from '@/store';
import { buildOpportunitySeeds } from './__fixtures__/opportunitySeeds';
import { UPDATABLE_FIELDS } from './opportunityResource';

// TEMPORARY stand-in for the medos opportunities resource in
// OPPORTUNITIES_DATA_CONTRACTS.md; delete once it is live. Records are kept
// in this browser's localStorage, one list per tenant, and a tenant's list
// starts from the demo seeds. Responses are deep copies.

const KEY_PREFIX = 'formulary-opportunities:v1:';

// Used only when localStorage is unavailable (e.g. blocked site data); records
// then last until a reload.
const memory = new Map();

const copy = (value) => JSON.parse(JSON.stringify(value));
const now = () => new Date().toISOString();
const keyFor = () => `${KEY_PREFIX}${store.getters.tenant?.id || 'none'}`;

let persistent = null;
function storageAvailable() {
  if (persistent === null) {
    try {
      const probe = `${KEY_PREFIX}probe`;
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      persistent = true;
    } catch {
      persistent = false;
      // eslint-disable-next-line no-console
      console.warn('localStorage is unavailable; opportunities will be lost on reload.');
    }
  }
  return persistent;
}

function readRaw(key) {
  return storageAvailable() ? window.localStorage.getItem(key) : (memory.get(key) ?? null);
}

// Throws on a failed write (e.g. quota), so the caller's save is reported.
function write(records) {
  const key = keyFor();
  const raw = JSON.stringify(records);
  if (storageAvailable()) window.localStorage.setItem(key, raw);
  else memory.set(key, raw);
}

// A tenant with nothing stored yet starts from the seeds; an emptied list stays empty.
function read() {
  const raw = readRaw(keyFor());
  if (raw !== null) {
    try {
      return JSON.parse(raw);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Stored opportunities were unreadable and have been reset:', error);
    }
  }
  const seeds = buildOpportunitySeeds();
  write(seeds);
  return seeds;
}

// lib/http's Request rejects with a message string, not an Error, so do the same.
const run = (operation) => {
  try {
    return Promise.resolve(operation());
  } catch (error) {
    return Promise.reject(String(error?.message || error));
  }
};

const notFound = (id) => {
  throw new Error(`Opportunity ${id} not found`);
};

// For tests: forget every tenant's records, so the next read starts from the seeds.
export function resetOpportunityLocalStore() {
  memory.clear();
  if (!storageAvailable()) return;
  Object.keys(window.localStorage)
    .filter((key) => key.startsWith(KEY_PREFIX))
    .forEach((key) => window.localStorage.removeItem(key));
}

// GET /opportunities[?status=], newest update first.
export function listOpportunities({ status } = {}) {
  return run(() => {
    const all = read()
      .filter((record) => !status || record.status === status)
      .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
    return { opportunities: copy(all) };
  });
}

// GET /opportunities/{id}
export function getOpportunity(id) {
  return run(() => {
    const record = read().find((r) => r.id === id) || notFound(id);
    return { opportunity: copy(record) };
  });
}

// POST /opportunities. `actor` stands in for the session user the server stamps.
export function createOpportunity(fields, actor) {
  return run(() => {
    const records = read();
    const at = now();
    const record = {
      ...copy(fields),
      id: `opp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      status: fields.status || 'saved',
      createdBy: actor || null,
      owner: actor || null,
      notes: [],
      createdAt: at,
      updatedAt: at,
    };
    write([...records, record]);
    return { opportunity: copy(record) };
  });
}

// PATCH /opportunities/{id}: only keys present change; `owner: null` unassigns.
export function updateOpportunity(id, patch) {
  return run(() => {
    const records = read();
    const record = records.find((r) => r.id === id) || notFound(id);
    UPDATABLE_FIELDS.forEach((key) => {
      if (key in patch) record[key] = copy(patch[key] ?? null);
    });
    record.updatedAt = now();
    write(records);
    return { opportunity: copy(record) };
  });
}

// DELETE /opportunities/{id}
export function deleteOpportunity(id) {
  return run(() => {
    const records = read();
    if (!records.some((r) => r.id === id)) notFound(id);
    write(records.filter((r) => r.id !== id));
    return { success: true };
  });
}

// POST /opportunities/{id}/notes
export function addOpportunityNote(id, content, actor) {
  return run(() => {
    const records = read();
    const record = records.find((r) => r.id === id) || notFound(id);
    const at = now();
    record.notes.push({ author: actor || null, content: String(content || ''), createdAt: at });
    record.updatedAt = at;
    write(records);
    return { opportunity: copy(record) };
  });
}

export default {
  listOpportunities,
  getOpportunity,
  createOpportunity,
  updateOpportunity,
  deleteOpportunity,
  addOpportunityNote,
  resetOpportunityLocalStore,
};
